import React, { useEffect, useState, useMemo } from 'react';
import Slide from '@material-ui/core/Slide';
import {
  Box,
  Button,
  Card,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useSelector, useDispatch } from 'react-redux';

import ToolBarTracking from 'src/app/components/ToolBarTracking';
import CarStatusBar from './CarStatusBar';
import { CAR_STATUS } from 'src/app/constant/config';
import { resetListVehicle } from 'src/features/vehicleSlice';
import {
  ButtonHideMenuTracking,
  ButtonShowMenuTracking
} from '../button/ButtonMenu';
import { useQuery } from 'src/app/hooks/useQuery';
import VehicleTreeTracking from 'src/app/components/tables/VehicleTableTracking';
import VehicleTableMini from 'src/app/components/tables/VehicleTableMini';
import { isSameVehicleStatus } from 'src/app/utils/vehicleService';

const HEADER_HEIGHT = 66;
const useMobileStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1300,
    background: '#fff',
    borderRadius: '12px 12px 0 0',
    boxShadow: '0 -4px 16px rgba(0,0,0,.15)',
    transition: 'height .25s ease'
  },
  collapsed: { height: 150 },
  expanded: { height: `calc(100dvh - ${HEADER_HEIGHT}px)` },
  handle: {
    height: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    touchAction: 'none'
  },
  handleBar: { width: 40, height: 4, borderRadius: 2, background: '#ccc' },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 12px 36px'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: '4px',
    touchAction: 'none'
  },
  listWrapper: { flex: 1, overflowY: 'auto', marginTop: '8px' },
  resizeHandle: {
    height: '12px',
    backgroundColor: '#f5f5f5',
    cursor: 'ns-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: '1px solid rgba(0,0,0,0.1)',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    marginTop: '4px',
    marginBottom: '4px',
    touchAction: 'none'
  },
  handleBarMini: { width: 40, height: 4, borderRadius: 2, background: '#bdbdbd' }
}));

/**
 * Dải "đang xem cái gì" kèm đúng một lối ra.
 *
 * Trang có hai lớp thu hẹp lồng nhau (lọc đại lý, chọn xe). Dải này hiện lớp TRONG CÙNG.
 *
 * Không phải lớp nào cũng có nút. Lớp xe có "Bỏ chọn xe"; lớp đại lý thì lối ra nằm trong cây —
 * bấm lại node đang mở để lùi một bậc lên đại lý cha — nên ở đây nó chỉ là NHÃN cho biết đang lọc
 * theo ai. `scope.action` rỗng là tín hiệu đó; đừng đổi thành nút mặc định.
 *
 * Vai trò user/driver không có cây đại lý, nhưng vẫn chọn được xe — nên chỉ ẩn lớp đại lý với họ,
 * không ẩn cả dải.
 */
const ScopeBar = ({ scope, onResetScope, isUserLike }) => {
  if (!scope || scope.kind === 'none') return null;
  if (scope.kind === 'agency' && isUserLike) return null;

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: isUserLike ? -3 : 8,
        marginBottom: isUserLike ? 8 : 0,
        padding: '6px 8px',
        borderRadius: 6,
        background: '#ffe0e6'
      }}
    >
      <Typography
        noWrap
        style={{ fontSize: 13, color: '#0C1132', minWidth: 0 }}
        title={scope.label}
      >
        Đang xem: <b>{scope.label}</b>
      </Typography>
      {scope.action ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon style={{ fontSize: 16 }} />}
          onClick={onResetScope}
          style={{
            flexShrink: 0,
            color: '#D9042F',
            borderColor: '#D9042F',
            textTransform: 'none',
            fontSize: 12,
            padding: '2px 8px',
            whiteSpace: 'nowrap'
          }}
        >
          {scope.action}
        </Button>
      ) : null}
    </Box>
  );
};

const MobileMenu = ({
  getVehicle,
  idSelected,
  listVehicleTable,
  currentCount,
  onNodeClick,
  search_agency_id,
  carStatus,
  setCarStatus,
  baseDeviceCount,
  onRefresh,
  extraCount = 0,
  isUserLike,
  totalCount,
  scope,
  onResetScope
}) => {
  const classes = useMobileStyles();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const title = isUserLike ? 'Danh sách thiết bị' : 'Danh sách đại lý/người dùng';

  const [bottomHeight, setBottomHeight] = useState(200); // Default height for bottom device table on mobile
  const [isResizing, setIsResizing] = useState(false);

  const [drawerHeight, setDrawerHeight] = useState(150);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);
  const dragStartHeightRef = React.useRef(150);
  const dragStartYRef = React.useRef(0);

  const isDraggingSheetRef = React.useRef(false);
  const currentDrawerHeightRef = React.useRef(150);
  const didDragRef = React.useRef(false);

  useEffect(() => {
    if (!isDraggingSheet) {
      const targetHeight = expanded ? window.innerHeight - HEADER_HEIGHT : 150;
      setDrawerHeight(targetHeight);
      currentDrawerHeightRef.current = targetHeight;
    }
  }, [expanded, isDraggingSheet]);

  const handleReset = () => {
    setCarStatus('');
    dispatch(resetListVehicle());
    if (onRefresh) onRefresh();
  };

  const handleGetVehicle = (vehicle) => {
    if (getVehicle) getVehicle(vehicle);
    if (vehicle) {
      setExpanded(false);
    }
  };

  const startResizing = React.useCallback((e) => {
    setIsResizing(true);
    if (e.cancelable) e.preventDefault();
  }, []);

  const handleSheetDragStart = (e) => {
    if (e.target.closest('.list_tracking_action')) {
      return;
    }
    if (e.touches && e.touches[0]) {
      isDraggingSheetRef.current = true;
      setIsDraggingSheet(true);
      didDragRef.current = false;
      dragStartYRef.current = e.touches[0].clientY;
      dragStartHeightRef.current = currentDrawerHeightRef.current;
    }
  };

  const handleSheetDragMove = (e) => {
    if (!isDraggingSheetRef.current) return;
    if (e.touches && e.touches[0]) {
      const clientY = e.touches[0].clientY;
      const deltaY = dragStartYRef.current - clientY;
      const newHeight = dragStartHeightRef.current + deltaY;
      const maxHeight = window.innerHeight - HEADER_HEIGHT;

      if (Math.abs(deltaY) > 5) {
        didDragRef.current = true;
      }

      const boundedHeight = Math.max(150, Math.min(maxHeight, newHeight));
      currentDrawerHeightRef.current = boundedHeight;
      setDrawerHeight(boundedHeight);
    }
  };

  const handleSheetDragEnd = () => {
    if (!isDraggingSheetRef.current) return;
    isDraggingSheetRef.current = false;
    setIsDraggingSheet(false);

    const maxHeight = window.innerHeight - HEADER_HEIGHT;
    const threshold = 150 + (maxHeight - 150) * 0.4;

    if (currentDrawerHeightRef.current > threshold) {
      setExpanded(true);
      currentDrawerHeightRef.current = maxHeight;
      setDrawerHeight(maxHeight);
    } else {
      setExpanded(false);
      currentDrawerHeightRef.current = 150;
      setDrawerHeight(150);
    }
  };

  const handleHandleClick = (e) => {
    if (didDragRef.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    setExpanded(v => !v);
  };

  const handleHeaderClick = (e) => {
    if (didDragRef.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (!expanded) {
      setExpanded(true);
    }
  };

  useEffect(() => {
    const handleMove = (clientY) => {
      const cardEl = document.getElementById('mobile-menu-card');
      if (!cardEl) return;
      const cardRect = cardEl.getBoundingClientRect();
      const newHeight = cardRect.bottom - clientY;

      const maxHeight = Math.max(150, cardRect.height - 300);
      if (newHeight > 100 && newHeight < maxHeight) {
        setBottomHeight(newHeight);
      }
    };

    const handleMouseMove = (e) => {
      if (!isResizing) return;
      handleMove(e.clientY);
    };

    const handleTouchMove = (e) => {
      if (!isResizing) return;
      if (e.touches && e.touches[0]) {
        handleMove(e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('touchend', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isResizing]);

  const showInnerContent = expanded || drawerHeight > 200;

  return (
    <>
      <Box
        id="mobile-menu-card"
        className={classes.root}
        style={{
          height: drawerHeight,
          transition: isDraggingSheet ? 'none' : 'height 0.25s ease'
        }}
      >
        <div
          className={classes.handle}
          onClick={handleHandleClick}
          onTouchStart={handleSheetDragStart}
          onTouchMove={handleSheetDragMove}
          onTouchEnd={handleSheetDragEnd}
        >
          <div className={classes.handleBar} />
        </div>

        <Box className={classes.content}>
          <Box
            className={classes.headerRow}
            onClick={handleHeaderClick}
            onTouchStart={handleSheetDragStart}
            onTouchMove={handleSheetDragMove}
            onTouchEnd={handleSheetDragEnd}
          >
            <Typography style={{ fontWeight: 600, fontSize: 14 }}>
              {title} ({currentCount + extraCount}/{listVehicleTable.length + extraCount})
            </Typography>
            <Box display="flex" alignItems="center">
              <span
                onClick={e => {
                  e.stopPropagation();
                  handleReset();
                }}
                className="list_tracking_action"
              >
                <img alt="sync" src="/static/iconSvg/sync.svg" />
              </span>
              <span
                onClick={e => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                className="list_tracking_action"
              >
                <img alt="info" src="/static/iconSvg/notice.svg" />
              </span>
            </Box>
          </Box>

          <ScopeBar
            scope={scope}
            onResetScope={onResetScope}
            isUserLike={isUserLike}
          />

          {!showInnerContent && (
            <Box mt={1}>
              <CarStatusBar
                carStatus={carStatus}
                setCarStatus={setCarStatus}
                hideLabel
                iconFolder="cars"
                iconSize={26}
                iconRotate={90}
              />
            </Box>
          )}

          {showInnerContent && (
            <>
              <ToolBarTracking />
              {!isUserLike && (
                <Box style={{ flex: 1, overflowY: 'auto', minHeight: 120, marginTop: '8px' }}>
                  <VehicleTreeTracking
                    vehicles={listVehicleTable}
                    getVehicle={handleGetVehicle}
                    carStatus={carStatus}
                    onNodeClick={onNodeClick}
                    rowActive={idSelected}
                    search_agency_id={search_agency_id}
                    showVehiclesInTree={false}
                  />
                </Box>
              )}
              {!isUserLike && (
                <div
                  className={classes.resizeHandle}
                  onMouseDown={startResizing}
                  onTouchStart={startResizing}
                >
                  <div className={classes.handleBarMini} />
                </div>
              )}
              <Box style={{
                height: isUserLike ? 'auto' : bottomHeight,
                flex: isUserLike ? 1 : 'none',
                minHeight: 100,
                marginTop: '8px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                <VehicleTableMini
                  vehicles={listVehicleTable}
                  getVehicle={handleGetVehicle}
                  idSelected={idSelected}
                  showSearch={!isUserLike}
                  total={totalCount}
                  extraCount={extraCount}
                  baseDeviceCount={baseDeviceCount}
                />
              </Box>
              <CarStatusBar
                carStatus={carStatus}
                setCarStatus={setCarStatus}
                hideLabel
                iconFolder="cars"
                iconSize={26}
                iconRotate={90}
              />
            </>
          )}
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box p={2} width={300}>
          <Typography align="center" gutterBottom>
            <b>Chú thích</b>
          </Typography>
          <List>
            {CAR_STATUS.map((car, i) => (
              <React.Fragment key={i}>
                <ListItem>
                  <ListItemAvatar>
                    <img
                      alt=""
                      src={`/static/iconSvg/carsv2/${car.icon}.svg`}
                    />
                  </ListItemAvatar>
                  <Typography variant="body2">{car.info}</Typography>
                </ListItem>
                {i < CAR_STATUS.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};

const useDesktopStyles = makeStyles(theme => ({
  typography: { padding: theme.spacing(2), width: '375px' },
  listInformation: {
    width: '100%',
    maxWidth: 375,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper
  },
  popoverHeader: { textAlign: 'center' },
  textInforCar: {
    color: '#0C1132 !important',
    fontSize: '14px !important',
    lineHeight: '19.6px',
    fontWeight: 400,
    padding: '15px 0'
  },
  syncIcon: {
    transition: 'transform 0.3s ease',
    '&.clicked': {
      animation: '$pulse 0.5s ease-in-out'
    },
    '& img': {
      transition: 'filter 0.3s ease'
    },
    '&.clicked img': {
      animation: '$flashRed 0.5s ease-in-out'
    }
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)'
    },
    '50%': {
      transform: 'scale(1.2)'
    },
    '100%': {
      transform: 'scale(1)'
    }
  },
  '@keyframes flashRed': {
    '0%, 100%': {
      filter: 'none'
    },
    '50%': {
      filter:
        'brightness(0) saturate(100%) invert(17%) sepia(96%) saturate(4439%) hue-rotate(352deg) brightness(96%) contrast(103%)'
    }
  },
  resizeHandle: {
    height: '10px',
    backgroundColor: '#f5f5f5',
    cursor: 'ns-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: '1px solid rgba(0,0,0,0.1)',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#eeeeee'
    }
  },
  handleBar: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: '#bdbdbd'
  }
}));

const DesktopMenu = ({
  getVehicle,
  idSelected,
  carStatus,
  setCarStatus,
  showMenu,
  setShowMenu,
  totalCount,
  onNodeClick,
  search_agency_id,
  baseDeviceCount,
  onRefresh,
  extraCount = 0,
  scope,
  onResetScope
}) => {
  const classes = useDesktopStyles();
  const dispatch = useDispatch();
  const listVehicleTracking = useSelector(
    state => state.vehicleSlice.listVehicleTracking
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isIconClicked, setIsIconClicked] = React.useState(false);

  const query = useQuery();
  const license_plate = query.get('license_plate');
  const { isUser, isAdmin: isAdminRole, isAgency, isDriver, isCustomerCare } = useSelector(
    state => state.authSlice
  );
  const isAdmin = isAdminRole || isCustomerCare;
  const isUserLike = isUser || isDriver;

  // Tính trong lượt render thay cho effect + setState: mỗi nhịp WebSocket chỉ còn một lượt render.
  const listVehicleTable = useMemo(() => {
    const source = listVehicleTracking || [];
    if (carStatus) {
      return source.filter(
        vehicle => isSameVehicleStatus(vehicle.device_status, carStatus) && vehicle.debt === false
      );
    }
    if (isUserLike && license_plate) {
      return source.filter(v => {
        const vData = v.vehicle || v;
        const lp = vData.license_plate || v.license_plate || '';
        return lp.toUpperCase().includes(license_plate.toUpperCase());
      });
    }
    return source;
  }, [carStatus, listVehicleTracking, isUserLike, license_plate]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseInfo = () => {
    setAnchorEl(null);
  };

  const handleReset = () => {
    setCarStatus('');
    dispatch(resetListVehicle());
    if (onRefresh) onRefresh();
    setIsIconClicked(true);
    setTimeout(() => {
      setIsIconClicked(false);
    }, 500);
  };

  const [bottomHeight, setBottomHeight] = React.useState(350);
  const [isResizing, setIsResizing] = React.useState(false);

  const startResizing = React.useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const cardRect = document.getElementById('menu-card')?.getBoundingClientRect();
      if (!cardRect) return;

      const newHeight = cardRect.bottom - e.clientY;
      if (newHeight > 100 && newHeight < cardRect.height - 150) {
        setBottomHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const open = Boolean(anchorEl);
  const iconInfo = open ? 'notice' : 'notice_gray';
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Slide
        direction="right"
        in={showMenu}
        style={{
          height: '100%',
          borderRadius: '8px',
          zIndex: 450,
          boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }}
      >
        <Card
          id="menu-card"
          style={{
            paddingTop: '50px',
            padding: '1em',
            width: '384px',
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <ToolBarTracking />

          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '14px',
              marginBottom: '0px'
            }}
          >
            <Typography
              style={{ color: '#0C1132', fontSize: '16px', fontWeight: 600 }}
            >
              {isAdmin || isAgency ? `Danh sách đại lý/người dùng: ${totalCount}` : `Danh sách thiết bị: ${totalCount}`}
            </Typography>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span
                  className={`${classes.syncIcon} list_tracking_action ${isIconClicked ? 'clicked' : ''
                    }`}
                  onClick={handleReset}
                >
                  <img alt="icon" src={`/static/iconSvg/sync.svg`} />{' '}
                </span>
                <span
                  className="list_tracking_action"
                  aria-describedby={id}
                  onClick={handleClick}
                >
                  <img alt="icon" src={`/static/iconSvg/${iconInfo}.svg`} />
                </span>
              </Box>

              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  <Typography className={classes.typography}>
                    <div className={classes.popoverHeader}>
                      <b> Chú thích </b>
                      <span className="float-right">
                        <img
                          alt="button_close"
                          className="list_tracking_action"
                          onClick={handleClose}
                          width="12px"
                          height="12px"
                          src="/static/iconSvg/close-btn.svg"
                        />
                      </span>
                    </div>

                    <List className={classes.listInformation}>
                      {CAR_STATUS &&
                        CAR_STATUS.map((car, index) => (
                          <>
                            <ListItem>
                              <ListItemAvatar>
                                <img
                                  alt="icon"
                                  src={`/static/iconSvg/carsv2/${car?.icon}.svg`}
                                  className="imageCarInfo"
                                />
                              </ListItemAvatar>
                              <span className={classes.textInforCar}>
                                {car?.info}
                              </span>
                            </ListItem>
                            {CAR_STATUS.length !== index + 1 ? (
                              <Divider variant="inset" component="li" />
                            ) : null}
                          </>
                        ))}
                    </List>
                  </Typography>
                </Popover>
              </Box>
            </Box>
          </Box>

          {/* Người dùng/tài xế: thanh thống kê trạng thái dưới tiêu đề "Danh sách thiết bị" */}
          {isUserLike && (
            <Box style={{ marginTop: '0px' }}>
              <CarStatusBar
                carStatus={carStatus}
                setCarStatus={setCarStatus}
                hideLabel
                iconFolder="cars"
                iconSize={26}
                iconRotate={90}
              />
            </Box>
          )}

          <ScopeBar
            scope={scope}
            onResetScope={onResetScope}
            isUserLike={isUserLike}
          />

          {!isUserLike && (
            <>
              <Box style={{ flex: 1, overflowY: 'hidden', marginTop: '0px' }}>
                <VehicleTreeTracking
                  vehicles={listVehicleTable}
                  getVehicle={getVehicle}
                  carStatus={carStatus}
                  onNodeClick={onNodeClick}
                  rowActive={idSelected}
                  search_agency_id={search_agency_id}
                  showVehiclesInTree={false}
                />
              </Box>

              <div
                className={classes.resizeHandle}
                onMouseDown={startResizing}
              >
                <div className={classes.handleBar} />
              </div>
            </>
          )}

          <Box style={{
            height: isUserLike ? 'auto' : bottomHeight,
            flex: isUserLike ? 1 : 'none',
            marginTop: isUserLike ? '-3px' : '0px',
            minHeight: 100,
            overflow: 'hidden',
            backgroundColor: '#fff',
            borderTop: isUserLike ? 'none' : '1px solid #ddd'
          }}>
            <VehicleTableMini
              vehicles={listVehicleTable}
              getVehicle={getVehicle}
              idSelected={idSelected}
              showSearch={!isUserLike}
              total={totalCount}
              extraCount={extraCount}
              baseDeviceCount={baseDeviceCount}
              carStatus={carStatus}
              setCarStatus={setCarStatus}
            />
          </Box>

          <ButtonShowMenuTracking
            showMenu={showMenu}
            setShowMenu={setShowMenu}
          />
        </Card>
      </Slide>

      <ButtonHideMenuTracking showMenu={showMenu} setShowMenu={setShowMenu} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseInfo}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography className={classes.typography}>
          <div className={classes.popoverHeader}>
            <b>Chú thích</b>
            <span className="float-right">
              <img
                alt="close"
                className="list_tracking_action"
                onClick={handleCloseInfo}
                width="12px"
                height="12px"
                src="/static/iconSvg/close-btn.svg"
              />
            </span>
          </div>

          <List className={classes.listInformation}>
            {CAR_STATUS.map((car, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemAvatar>
                    <img
                      alt="icon"
                      src={`/static/iconSvg/carsv2/${car?.icon}.svg`}
                      className="imageCarInfo"
                    />
                  </ListItemAvatar>
                  <span className={classes.textInforCar}>{car?.info}</span>
                </ListItem>
                {CAR_STATUS.length !== index + 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        </Typography>
      </Popover>
    </>
  );
};

const MenuBoxTracking = ({
  getVehicle,
  idSelected,
  setShowMenu,
  showMenu,
  setCarStatus,
  carStatus,
  onNodeClick,
  search_agency_id,
  totalDeviceCount,
  baseDeviceCount,
  onRefresh,
  extraCount = 0,
  scope,
  onResetScope
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isUser, isDriver } = useSelector(
    state => state.authSlice
  );
  const isUserLike = isUser || isDriver;

  const listVehicleTracking = useSelector(
    state => state.vehicleSlice.listVehicleTracking
  );

  // Tính trong lượt render thay cho effect + 2 lần setState.
  const listVehicleTable = useMemo(
    () =>
      carStatus
        ? listVehicleTracking.filter(v => isSameVehicleStatus(v.device_status, carStatus))
        : listVehicleTracking,
    [listVehicleTracking, carStatus]
  );
  const currentCount = listVehicleTable.length;

  const commonProps = React.useMemo(() => ({
    getVehicle,
    idSelected,
    listVehicleTable,
    currentCount,
    onNodeClick,
    search_agency_id,
    totalCount: totalDeviceCount ?? (listVehicleTracking?.length || 0),
    carStatus,
    setCarStatus,
    extraCount,
    baseDeviceCount,
    onRefresh,
    isUserLike,
    scope,
    onResetScope
  }), [
    getVehicle,
    idSelected,
    carStatus,
    setCarStatus,
    listVehicleTable,
    currentCount,
    onNodeClick,
    search_agency_id,
    listVehicleTracking,
    totalDeviceCount,
    extraCount,
    baseDeviceCount,
    onRefresh,
    isUserLike,
    scope,
    onResetScope
  ]);

  return isMobile ? (
    <MobileMenu {...commonProps} />
  ) : (
    <DesktopMenu
      {...commonProps}
      showMenu={showMenu}
      setShowMenu={setShowMenu}
    />
  );
};

export default React.memo(MenuBoxTracking);
